import React from "react";

import type { TestResult, TestStatus } from "./types";
import { explorerTxUrl } from "./explorerLink";

interface TestRowProps {
  label: string;
  hint?: string;
  result: TestResult;
  onRun: () => void;
  disabled?: boolean;
}

export function TestRow({
  label,
  hint,
  result,
  onRun,
  disabled,
}: TestRowProps): JSX.Element {
  return (
    <div style={rowStyle}>
      <div style={{ flex: "0 0 auto" }}>
        <button
          onClick={onRun}
          disabled={disabled || result.status === "running"}
          style={runBtnStyle(disabled || result.status === "running")}
        >
          Run
        </button>
      </div>
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={labelStyle}>{label}</div>
        {hint && <div style={hintStyle}>{hint}</div>}
        {result.output && <div style={outputStyle}>{result.output}</div>}
        {result.txSig && (
          <a
            href={explorerTxUrl(result.txSig)}
            target="_blank"
            rel="noreferrer"
            style={linkStyle}
          >
            tx: {result.txSig.slice(0, 8)}…{result.txSig.slice(-6)}
          </a>
        )}
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <StatusPill status={result.status} />
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: TestStatus }): JSX.Element {
  const { bg, fg, label } = pillTone(status);
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 10,
        background: bg,
        color: fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        minWidth: 56,
        textAlign: "center",
      }}
    >
      {label}
    </span>
  );
}

function pillTone(status: TestStatus): { bg: string; fg: string; label: string } {
  switch (status) {
    case "running":
      return { bg: "#23344f", fg: "#7ab0ff", label: "RUNNING" };
    case "ok":
      return { bg: "#1c3a26", fg: "#7af09e", label: "OK" };
    case "fail":
      return { bg: "#3a1c26", fg: "#f47a8e", label: "FAIL" };
    default:
      return { bg: "#22222e", fg: "#9aa0b4", label: "IDLE" };
  }
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: "8px 10px",
  borderBottom: "1px solid #1c1c26",
  background: "#0e0e15",
};

const labelStyle: React.CSSProperties = {
  color: "#e8e8ef",
  fontSize: 12,
  fontWeight: 600,
};

const hintStyle: React.CSSProperties = {
  color: "#7a8098",
  fontSize: 11,
  marginTop: 2,
};

const outputStyle: React.CSSProperties = {
  color: "#cfd2dc",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  marginTop: 4,
  wordBreak: "break-word",
  whiteSpace: "pre-wrap",
};

const linkStyle: React.CSSProperties = {
  display: "inline-block",
  marginTop: 4,
  color: "#7ab0ff",
  fontSize: 11,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  textDecoration: "underline",
};

function runBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "4px 10px",
    background: disabled ? "#22222e" : "#3a3a4e",
    color: disabled ? "#5a5a72" : "#e8e8ef",
    border: "1px solid #2a2a36",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
  };
}
